/**
 * Books and broker imports — `/sizing/*` on qv-data.
 *
 * The journal's rules, for the journal's reasons (`api/journal.ts`): not routed
 * through the persistence hook, every call **throws** on failure so the screen
 * can say so and offer it again, and each returns the stored state rather than
 * void because the server is what parsed the file.
 *
 * The shapes here are this API's answers, not contract entities — a book with
 * its counts, a staged upload with its duplicates — so they are declared here,
 * like `api/admin.ts` does, rather than in `@qufolio/model`.
 *
 * `docs/plans/position-sizing.md` is the design; nothing here sizes or places
 * an order.
 */

import { can } from "../lib/entitlements";
import { currentUserId, requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = httpBase(import.meta.env.VITE_QV_SET_URL as string | undefined);

/** Whether books and imports are offered. Off with no server or no `feat:sizing`. */
export function sizingEnabled(): boolean {
  return BASE !== "" && can("feat:sizing");
}

export type BookKind = "import" | "paper" | "journal";

export interface Book {
  id: string;
  name: string;
  kind: BookKind;
  broker: string;
  startingBalance: number;
  /** Closed round trips, orphans excluded. */
  trades: number;
  open: number;
  fills: number;
  plans: number;
  imports: number;
  firstFill?: string;
  lastFill?: string;
  lastImport?: string;
}

/** The fields a column mapping can name — `app/sizing/normalize.py` `FIELDS`. */
export const MAPPING_FIELDS = [
  "date", "time", "symbol", "action", "quantity", "price",
  "fees", "commission", "fillId", "description", "right", "strike", "expiry",
] as const;
export type MappingField = (typeof MAPPING_FIELDS)[number];
export const REQUIRED_FIELDS: readonly MappingField[] = ["date", "symbol", "quantity", "price"];

export interface ColumnMapping {
  columns: Partial<Record<MappingField, string | null>>;
  timezone: string;
}

export interface Duplicate {
  row: number;
  match: "fillId" | "hash";
  symbol: string;
  side: string | null;
  effect: string | null;
  quantity: number;
  price: number;
  executedAt: string;
  decision: "skip" | "keep";
}

export interface ImportSummary {
  fillsImported: number;
  duplicatesSkipped: number;
  duplicatesKept: number;
  roundTrips: number;
  stillOpen: number;
  orphans: number;
  undefinedRisk: number;
  expiredAssignedExercised: number;
  problems: { row: number; reason: string }[];
  skipped: Record<string, number>;
  pnlBasis: string;
}

export interface StagedImport {
  id: string;
  status: "staged" | "committed";
  broker: string;
  filename: string;
  bookId?: string | null;
  newBook?: string;
  headers?: string[];
  headerSig?: string;
  mapping?: ColumnMapping;
  /** True when these headers were mapped before and the saved answer was used. */
  mappingSaved?: boolean;
  /** Required fields the mapping does not name yet. Empty means it parsed. */
  missing?: MappingField[];
  preview?: string[][];
  rows?: number;
  problems?: { row: number; reason: string }[];
  skipped?: Record<string, number>;
  duplicates?: Duplicate[];
  counts?: { fills: number; stock: number; option: number; roundTrips: number };
  range?: [string, string];
  summary?: ImportSummary;
  book?: Book;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  if (!BASE) throw new Error("no server configured");
  // Whose books, threaded here for `api/journal.ts`'s reason: one call site
  // that forgot it would read somebody else's.
  const separator = path.includes("?") ? "&" : "?";
  const url = `${BASE}${path}${separator}userId=${encodeURIComponent(currentUserId())}`;
  const auth = await requestHeaders();
  const response = await fetch(url, {
    ...init,
    headers: { "content-type": "application/json", ...auth, ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail ?? `sizing ${response.status}`);
  }
  return (await response.json()) as T;
}

export function listBooks(): Promise<Book[]> {
  return call("/sizing/books");
}

export function renameBook(id: string, name: string): Promise<Book> {
  return call(`/sizing/books/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
}

export function deleteBook(id: string): Promise<{ deleted: string }> {
  return call(`/sizing/books/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** Where an upload goes: an existing book, or a new one by name. */
export type Destination = { bookId: string } | { newBook: string };

/**
 * Upload a CSV. The file is the request body — the server takes no multipart —
 * and the destination rides the query string. Nothing reaches the ledger yet.
 */
export function uploadCsv(file: File, destination: Destination, broker = ""): Promise<StagedImport> {
  const params = new URLSearchParams({ filename: file.name });
  if ("bookId" in destination) params.set("bookId", destination.bookId);
  else params.set("newBook", destination.newBook);
  if (broker) params.set("broker", broker);
  return call(`/sizing/imports?${params.toString()}`, {
    method: "POST",
    body: file,
    headers: { "content-type": "text/csv" },
  });
}

/** Change the mapping, the broker label or the destination; the file is re-read. */
export function updateImport(
  id: string,
  patch: { mapping?: ColumnMapping; broker?: string } & Partial<{ bookId: string; newBook: string }>,
): Promise<StagedImport> {
  return call(`/sizing/imports/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

/** Write the kept fills. `decisions` is row -> keep|skip for each duplicate. */
export function commitImport(id: string, decisions: Record<string, "keep" | "skip">): Promise<StagedImport> {
  return call(`/sizing/imports/${encodeURIComponent(id)}/commit`, {
    method: "POST",
    body: JSON.stringify({ decisions }),
  });
}

export function cancelImport(id: string): Promise<{ cancelled: string }> {
  return call(`/sizing/imports/${encodeURIComponent(id)}`, { method: "DELETE" });
}
